import { useMemo } from 'react';
import { parseDate, type CalendarDate, type Time } from '@internationalized/date';
import { format } from 'date-fns';
import type * as yup from 'yup';
import type { ModuleIntakeTerm } from '@/entities/Courses';

const MINUTES_PER_DAY = 24 * 60;

export interface TermDateBounds {
  start: CalendarDate;
  end: CalendarDate;
}

/**
 * Passed to the session and homework forms as react-hook-form context: the yup tests below read it.
 * The backend checks the same intake term, so a date the form accepts is not rejected on save
 */
export interface SessionDatesContext {
  termBounds: TermDateBounds | null;
  termLabel: string;
  // Homework form only: the deadline can't be before the session
  sessionDate?: CalendarDate | null;
}

export const getTermDateBounds = (intakeTerm?: ModuleIntakeTerm | null): TermDateBounds | null => {
  if (!intakeTerm) return null;
  try {
    return {
      start: parseDate(intakeTerm.startDate.slice(0, 10)),
      end: parseDate(intakeTerm.endDate.slice(0, 10)),
    };
  } catch {
    return null;
  }
};

const formatCalendarDate = (date: CalendarDate) =>
  format(new Date(date.year, date.month - 1, date.day), 'd MMM yyyy');

/** "Term 2: 12 Jan 2026 – 30 Apr 2026" */
export const formatTermLabel = (
  intakeTerm: ModuleIntakeTerm | null | undefined,
  bounds: TermDateBounds | null,
) => {
  if (!bounds) return '';
  const range = `${formatCalendarDate(bounds.start)} – ${formatCalendarDate(bounds.end)}`;
  return intakeTerm?.number ? `Term ${intakeTerm.number}: ${range}` : `Term: ${range}`;
};

const laterOf = (a: CalendarDate, b: CalendarDate) => (a.compare(b) >= 0 ? a : b);

/** Hint under the session date: "Available dates: 16 Nov 2026 – 13 Feb 2027" */
export const formatSessionDateHint = (bounds: TermDateBounds | null) =>
  bounds
    ? `Available dates: ${formatCalendarDate(bounds.start)} – ${formatCalendarDate(bounds.end)}`
    : '';

/**
 * Hint under the deadline: from the session date (not before the term start) to the term end.
 * Without a session date yet: the term range, not before the session date
 */
export const formatDeadlineHint = (
  bounds: TermDateBounds | null,
  sessionDate: CalendarDate | null,
) => {
  if (!bounds) return '';
  const from = sessionDate ? laterOf(bounds.start, sessionDate) : bounds.start;
  const range = `${formatCalendarDate(from)} – ${formatCalendarDate(bounds.end)}`;
  return sessionDate
    ? `Available dates: ${range}`
    : `Available dates: ${range}, not before the session date`;
};

/** Bounds and label of the intake term for the session forms */
export const useSessionTermDates = (intakeTerm?: ModuleIntakeTerm | null) =>
  useMemo(() => {
    const termBounds = getTermDateBounds(intakeTerm);
    return { termBounds, termLabel: formatTermLabel(intakeTerm, termBounds) };
  }, [intakeTerm]);


/**
 * Earliest pickable date: the term start, and for a deadline not before `notBefore` (the session
 * date). Past dates are allowed: only the term limits the session. null — no lower limit
 */
const getMinPickableDate = (bounds: TermDateBounds | null, notBefore?: CalendarDate | null) => {
  if (bounds && notBefore) return laterOf(bounds.start, notBefore);
  return notBefore ?? bounds?.start ?? null;
};

/**
 * DatePicker limits of a session date or deadline, as the forms had with `minValue={today}`:
 * days outside the term (and a deadline before the session) are disabled
 */
export const getSessionDatePickerProps = (
  bounds: TermDateBounds | null,
  notBefore?: CalendarDate | null,
) => ({
  minValue: getMinPickableDate(bounds, notBefore) ?? undefined,
  maxValue: bounds?.end,
});

export const isOutsideTerm = (date: CalendarDate, bounds: TermDateBounds) =>
  date.compare(bounds.start) < 0 || date.compare(bounds.end) > 0;

/**
 * The slot (start + duration) ends after the last day of the term. scheduledAt keeps the entered
 * date and time as UTC, and the backend allows a slot to end until 00:00 after the term end
 */
export const isSlotEndAfterTerm = (
  date: CalendarDate,
  startTime: Time,
  durationMinutes: number,
  bounds: TermDateBounds,
) => {
  const minutesUntilTermEnd = (bounds.end.compare(date) + 1) * MINUTES_PER_DAY;
  return startTime.hour * 60 + startTime.minute + durationMinutes > minutesUntilTermEnd;
};

export const termDateMessage = (termLabel: string) => `Pick a date within ${termLabel}`;

export const slotEndMessage = (bounds: TermDateBounds) =>
  `The session must end by the end of the term (${formatCalendarDate(bounds.end)})`;

export const DEADLINE_BEFORE_SESSION_MESSAGE = "The deadline can't be before the session date";

const readContext = (context: unknown) => (context ?? {}) as Partial<SessionDatesContext>;

/** yup test of the session date: within the intake term */
export function testSessionDateWithinTerm(this: yup.TestContext, value: unknown) {
  const { termBounds, termLabel = '' } = readContext(this.options.context);
  if (!termBounds || !value) return true;
  return isOutsideTerm(value as CalendarDate, termBounds)
    ? this.createError({ message: termDateMessage(termLabel) })
    : true;
}

/** yup test of the start time: the slot (start + duration) ends within the intake term */
export function testSlotEndWithinTerm(this: yup.TestContext, value: unknown) {
  const { termBounds } = readContext(this.options.context);
  const { date, duration } = this.parent as { date?: CalendarDate | null; duration?: number };
  if (!termBounds || !value || !date || !duration) return true;
  return isSlotEndAfterTerm(date, value as Time, duration, termBounds)
    ? this.createError({ message: slotEndMessage(termBounds) })
    : true;
}

/** yup test of the assignment deadline: within the intake term and not before the session */
export function testDeadlineWithinTerm(this: yup.TestContext, value: unknown) {
  const { termBounds, termLabel = '', sessionDate } = readContext(this.options.context);
  if (!value) return true;
  const deadline = value as CalendarDate;
  if (termBounds && isOutsideTerm(deadline, termBounds)) {
    return this.createError({ message: termDateMessage(termLabel) });
  }
  if (sessionDate && deadline.compare(sessionDate) < 0) {
    return this.createError({ message: DEADLINE_BEFORE_SESSION_MESSAGE });
  }
  return true;
}
